import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Loader2, Droplet } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const FAMILY_LABELS = {
  mother: "Mother Sauce",
  emulsion: "Emulsion",
  reduction: "Reduction",
  condiment: "Condiment",
  vinaigrette: "Vinaigrette",
  modern: "Modern",
};

const FAMILY_COLOR = {
  mother: "text-orange-500",
  emulsion: "text-amber-400",
  reduction: "text-rose-500",
  condiment: "text-lime-500",
  vinaigrette: "text-yellow-500",
  modern: "text-sky-400",
};

const Sauces = () => {
  const [sauces, setSauces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [family, setFamily] = useState("all");

  useEffect(() => {
    api.get("/sauces")
      .then((res) => setSauces(res.data))
      .catch(() => setSauces([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(
    () => (family === "all" ? sauces : sauces.filter((s) => s.family === family)),
    [sauces, family]
  );

  const families = useMemo(() => {
    const set = new Set(sauces.map((s) => s.family));
    return Array.from(set);
  }, [sauces]);

  return (
    <div className="min-h-screen bg-stone-950 pt-16 pb-32">
      <section className="max-w-[1600px] mx-auto px-6 sm:px-10 pt-16">
        <div className="mb-16 border-b border-stone-800 pb-12">
          <div className="flex items-center gap-4 mb-6">
            <span className="magazine-number text-5xl">04</span>
            <div className="w-16 h-px bg-orange-600" />
            <span className="eyebrow">The Sauce Larder</span>
          </div>
          <h1 className="font-serif-display text-6xl md:text-8xl text-stone-100 leading-[0.85] tracking-tighter">
            Where the <span className="italic text-orange-600">magic</span>
            <br />
            actually happens.
          </h1>
          <p className="mt-8 max-w-2xl text-stone-300 leading-relaxed">
            Every serious cook keeps a mental library of sauces. Five mothers, a dozen
            children, a handful of upstarts from Oaxaca, Hong Kong and Catalonia. Master
            these, and you can dress any protein, any vegetable, any night of the week.
          </p>
        </div>

        {/* Family filter */}
        <div className="mb-10 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => setFamily("all")}
            data-testid="sauce-filter-all"
            className={`px-4 py-2 uppercase tracking-[0.2em] text-[10px] border transition-colors ${
              family === "all"
                ? "border-orange-600 text-orange-500"
                : "border-stone-800 text-stone-400 hover:border-stone-700"
            }`}
          >
            All
          </button>
          {families.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFamily(f)}
              data-testid={`sauce-filter-${f}`}
              className={`px-4 py-2 uppercase tracking-[0.2em] text-[10px] border transition-colors ${
                family === f
                  ? "border-orange-600 text-orange-500"
                  : "border-stone-800 text-stone-400 hover:border-stone-700"
              }`}
            >
              {FAMILY_LABELS[f] || f}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-32">
            <Loader2 className="w-6 h-6 animate-spin text-orange-600" />
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="sauces-grid">
            {filtered.map((s, i) => (
              <Link
                key={s.id}
                to={`/sauces/${s.slug}`}
                data-testid={`sauce-card-${s.slug}`}
                className="group relative bg-stone-900/40 border border-stone-800 hover:border-orange-800 overflow-hidden transition-colors"
              >
                {s.image_status === "ready" && (
                  <div className="aspect-[4/3] bg-stone-900 relative overflow-hidden">
                    <img
                      src={`${BACKEND}/api/sauces/${s.slug}/image`}
                      alt={s.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-stone-950/80 via-transparent to-transparent" />
                  </div>
                )}
                <div className="p-8">
                  <div className="flex items-center justify-between mb-6">
                    <span className={`magazine-number text-3xl ${FAMILY_COLOR[s.family] || "text-orange-500"}`}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className={`text-[9px] uppercase tracking-[0.28em] ${FAMILY_COLOR[s.family] || "text-orange-500"}`}>
                      {FAMILY_LABELS[s.family] || s.family}
                    </span>
                  </div>
                  <h3 className="font-serif-display text-3xl text-stone-100 leading-[1] tracking-tight mb-3">
                    {s.name}
                  </h3>
                  <p className="font-serif-display italic text-lg text-stone-400 leading-snug mb-6">
                    {s.subtitle}
                  </p>
                  <div className="flex items-center gap-3 pt-6 border-t border-stone-800">
                    <Droplet className="w-3.5 h-3.5 text-orange-600" strokeWidth={1.5} />
                    <span className="text-[10px] uppercase tracking-[0.24em] text-stone-500">
                      {s.origin}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default Sauces;
